// A phone's bar at the foot of the screen: the boards list, All boards, the chat, Connect, and More (the look, sharing,
// settings, adding to the board, the account). The boards list and More open as sheets over the board.

import { useState } from "react";
import { flushSync } from "react-dom";
import { useShallow } from "zustand/react/shallow";
import { Archive, ChevronDown, RotateCcw, Calendar, CalendarDays, Check, Compass, CreditCard, FileText, FolderOpen, Kanban, LayoutDashboard, LayoutGrid, LogOut, Menu, MessageCircle, Network, Palette, Pencil, Plug, Plus, Presentation, Rows3, ScrollText, Settings2, Share2, StickyNote, Table2, Trash2, UserRound, CircleHelp, WandSparkles } from "lucide-react";
import type { Board, BoardFolder, BoardKind } from "../../shared/types";
import { topLevel, type ListEntry } from "../../shared/boardsOrder";
import { BOARD_KINDS, kindOf } from "../../shared/boardKinds";
import { cx } from "../lib/util";
import { currentBoard, get, set, useStore } from "../state/store";
import { addWidget, archiveBoard, archivedOn, deleteBoard, restoreBoard, switchBoard } from "../state/actions";
import { signOut } from "../state/sync";
import { DEMO, START_URL } from "../demo/mode";
import { openChat, useChat } from "../agent/store";
import { askLabel, askWho, useBuilder } from "../agent/handoff";
import { openOverview } from "../chrome/Panels";
import { Sheet, SheetRow } from "./Sheet";
import { BoardCount } from "../chrome/Bell";
import { closeSheet, openSheet, usePhone, type PhoneSheet } from "./state";
import { sharedHref } from "../state/guest";
import { renameBoard } from "../boards/Rename";

const KIND_ICON: Record<BoardKind, typeof LayoutDashboard> = { canvas: LayoutDashboard, doc: FileText, mindmap: Network, calendar: CalendarDays, pipeline: Kanban, plan: Compass, files: FolderOpen, sheet: Table2, slides: Presentation, agenda: Calendar, custom: WandSparkles };

export function BoardMark({ board, size = 16 }: { board: Board; size?: number }) {
  if (board.emoji) return <span className="pbar-emoji">{board.emoji}</span>;
  const Icon = KIND_ICON[kindOf(board)] ?? LayoutDashboard;
  return <Icon size={size} />;
}

/**
 * The bar, and the sheets it opens. All boards keeps the bar (Fixes 9): it draws its own copy over its tiles
 * (inAllBoards), since it lies over the board's column and everything in it, and the column's copy steps aside. The
 * bar's other places leave All boards as they open, and All boards again leaves it.
 */
export function PhoneBar({ inAllBoards = false }: { inAllBoards?: boolean }) {
  const sheet = usePhone((s) => s.sheet);
  const chatOpen = useChat((s) => s.open);
  const panel = useStore((s) => s.panel);
  const builder = useBuilder();
  const over = panel === "boards";
  const leave = () => over && set({ panel: null, panelArg: null });
  if (inAllBoards !== over) return inAllBoards ? null : <Sheets sheet={sheet} />;
  return (
    <>
      {/* only what is open is lit (Fixes 9): the chat's accent is its icon at rest, its fill only while it is open */}
      <nav className={cx("pbar", over && "is-over")} aria-label="Board">
        <button
          className={cx("pbar-btn", sheet === "boards" && "is-on")}
          onClick={() => {
            leave();
            openSheet("boards");
          }}
          aria-label="Boards"
        >
          <span className="pbar-ico"><Rows3 size={20} /></span>
          <span>Boards</span>
        </button>
        <button className={cx("pbar-btn mobile-boards", over && "is-on")} onClick={() => (over ? leave() : (closeSheet(), openOverview()))} aria-label="All boards" aria-pressed={over}>
          <span className="pbar-ico"><LayoutGrid size={20} /></span>
          <span>All boards</span>
        </button>
        <button
          className={cx("pbar-ask mobile-chat", chatOpen && "is-on")}
          onClick={() => {
            leave();
            openChat();
          }}
          aria-label={askLabel(builder)}
        >
          <span className="pbar-ask-dot">
            <MessageCircle size={19} />
          </span>
          <span>{short(askWho(builder))}</span>
        </button>
        <button className={cx("pbar-btn", panel === "connect" && "is-on")} onClick={() => set({ panel: "connect", panelArg: { tab: "data" } })} aria-label="Connect">
          <span className="pbar-ico"><Plug size={20} /></span>
          <span>Connect</span>
        </button>
        <button
          className={cx("pbar-btn", sheet === "more" && "is-on")}
          onClick={() => {
            leave();
            openSheet("more");
          }}
          aria-label="More"
        >
          <span className="pbar-ico"><Menu size={20} /></span>
          <span>More</span>
        </button>
      </nav>
      {!inAllBoards && <Sheets sheet={sheet} />}
    </>
  );
}

function Sheets({ sheet }: { sheet: PhoneSheet }) {
  return sheet === "boards" ? <BoardsSheet /> : sheet === "more" ? <MoreSheet /> : null;
}

/** "Ask Claude", "Ask Codex": the words under the chat's button, or just "Ask" when the name is too long for the bar. */
function short(who: string): string {
  return who.length > 7 ? "Ask" : `Ask ${who}`;
}

/** The list's top level with each run of boards in no folder together, for a section each. */
function runs(lines: ListEntry[]): ({ folder: BoardFolder; boards: Board[] } | { loose: Board[] })[] {
  const out: ({ folder: BoardFolder; boards: Board[] } | { loose: Board[] })[] = [];
  for (const e of lines) {
    const last = out.at(-1);
    if ("folder" in e) out.push(e);
    else if (last && "loose" in last) last.loose.push(e.board);
    else out.push({ loose: [e.board] });
  }
  return out;
}

function BoardsSheet() {
  const boards = useStore((s) => s.boards);
  // archived boards (Features 1, 20 Oct): at the foot, folded shut with their count; one opens read only, with Restore
  const shelf = useStore((s) => s.archived);
  const [shelfOpen, setShelfOpen] = useState(false);
  // boards others shared with this person (Features 1, sharing)
  const sharedList = useStore((s) => s.shared);
  const boardId = useStore((s) => s.boardId);
  const folders = useStore(useShallow((s) => s.workspace?.folders ?? []));
  const go = (id: string) => {
    closeSheet();
    switchBoard(id);
    document.querySelector(".mobile")?.scrollTo({ top: 0 });
  };
  const row = (b: Board) => (
    <SheetRow key={b.id} icon={<BoardMark board={b} />} label={<>{b.name} <BoardCount boardId={b.id} /></>} hint={b.id === boardId ? <Check size={16} /> : kindOf(b) === "canvas" ? undefined : BOARD_KINDS[kindOf(b)].name} on={b.id === boardId} onClick={() => go(b.id)} className="psheet-board" />
  );
  return (
    <Sheet
      label="Boards"
      onClose={closeSheet}
      className="psheet-boards"
      keep="boards"
      title={
        <>
          <b>Boards</b>
          <button
            className="btn btn-sm"
            onClick={() => {
              closeSheet();
              set({ panel: "newboard" });
            }}
          >
            <Plus size={14} /> New board
          </button>
        </>
      }
    >
      {/* in the list's one order (Round 6): a folder with its boards, and each run of boards in none, as the drop-down has them */}
      {runs(topLevel(folders, boards)).map((run) =>
        "folder" in run ? (
          run.boards.length > 0 && (
            <section key={run.folder.id} className="psheet-group">
              <h4>{run.folder.name}</h4>
              {run.boards.map(row)}
            </section>
          )
        ) : (
          <section key={run.loose[0].id} className="psheet-group">
            {run.loose.map(row)}
          </section>
        ),
      )}
      {sharedList.length > 0 && (
        <section className="psheet-group">
          <h4>Shared with you</h4>
          {sharedList.map((b) => (
            <SheetRow key={`${b.ws}.${b.board}`} icon={<span className="psheet-emoji">{b.emoji || "▦"}</span>} label={b.name || "A board"} hint={b.role === "edit" ? "Can edit" : "Can view"} onClick={() => (location.href = sharedHref(b))} className="psheet-board is-shared" />
          ))}
        </section>
      )}
      {shelf.length > 0 && (
        <section className="psheet-group psheet-shelf">
          <SheetRow icon={<Archive size={18} />} label={`Archived · ${shelf.length}`} hint={<ChevronDown size={16} className={cx("psheet-shelf-chev", shelfOpen && "is-open")} />} onClick={() => setShelfOpen(!shelfOpen)} />
          {shelfOpen && shelf.map((b) => <SheetRow key={b.id} icon={<BoardMark board={b} />} label={b.name} hint={b.id === boardId ? <Check size={16} /> : archivedOn(b.archived!)} on={b.id === boardId} onClick={() => go(b.id)} className="psheet-board is-archived" />)}
        </section>
      )}
    </Sheet>
  );
}

function MoreSheet() {
  const board = useStore(currentBoard);
  const boards = useStore((s) => s.boards);
  const ws = useStore((s) => s.workspace);
  if (!board) return null;
  const canvas = kindOf(board) === "canvas";
  const go = (fn: () => void) => () => {
    closeSheet();
    fn();
  };
  return (
    <Sheet
      label="More"
      onClose={closeSheet}
      title={
        <span className="psheet-account">
          <span className="psheet-avatar">
            <UserRound size={16} />
          </span>
          <span>
            <b>{ws?.name ?? "Your board"}</b>
            <small>{DEMO ? "A demo. Nothing you change is saved." : (ws?.email ?? "Saved on this device only")}</small>
          </span>
        </span>
      }
    >
      {canvas && (
        <section className="psheet-group">
          <h4>Add to {board.name}</h4>
          <SheetRow icon={<StickyNote size={18} />} label="Sticky note" onClick={addSticky} />
          <SheetRow icon={<Plus size={18} />} label="A widget" hint="From the gallery" onClick={go(() => set({ panel: "gallery" }))} />
        </section>
      )}
      <section className="psheet-group">
        <h4>This board</h4>
        {board.archived ? (
          <SheetRow icon={<RotateCcw size={18} />} label="Restore this board" hint={`Archived on ${archivedOn(board.archived)}`} onClick={go(() => restoreBoard(board.id))} />
        ) : (
          <>
            {/* in the board menu's order (Features 4, Tidy): Rename (Fixes 9), Share, Look and themes, its instructions, then Archive */}
            <SheetRow icon={<Pencil size={18} />} label="Rename this board" onClick={go(() => renameBoard(board.id))} />
            <SheetRow icon={<Share2 size={18} />} label="Share" hint={board.share.enabled ? "Shared publicly" : undefined} onClick={go(() => set({ panel: "share" }))} />
            <SheetRow icon={<Palette size={18} />} label="Look and themes" onClick={go(() => set({ panel: "theme" }))} />
            {/* how agents keep it (Features 3): "On" while it has instructions; nothing waits to review (Features 4) */}
            <SheetRow icon={<ScrollText size={18} />} label="Instructions for agents" hint={board.instructions ? "On" : undefined} onClick={go(() => set({ panel: "instructions", panelArg: null }))} />
            <SheetRow icon={<Archive size={18} />} label="Archive this board" disabled={boards.length <= 1} onClick={go(() => archiveBoard(board.id))} />
          </>
        )}
        {/* to the trash, with Undo on its receipt (Fixes 9): an archived board too; the last one in use stays */}
        <SheetRow icon={<Trash2 size={18} />} label="Delete this board" danger disabled={!board.archived && boards.length <= 1} onClick={go(() => deleteBoard(board.id))} />
      </section>
      <section className="psheet-group">
        <h4>Account</h4>
        {/* help for the board in view (Features 4) */}
        <SheetRow icon={<CircleHelp size={18} />} label="Help" hint="For the board you're on" onClick={go(() => set({ panel: "help", panelArg: null }))} />
        <SheetRow icon={<Settings2 size={18} />} label="Settings" onClick={go(() => set({ panel: "settings" }))} />
        {/* the demo has no account: its way out is a board of your own (src/demo) */}
        {DEMO ? (
          <SheetRow icon={<Plus size={18} />} label="Start your own board" onClick={() => void (location.href = START_URL)} />
        ) : (
          <>
            {ws?.email ? (
              <SheetRow icon={<CreditCard size={18} />} label="My subscription" onClick={go(() => set({ panel: "settings", panelArg: { tab: "account" } }))} />
            ) : (
              <SheetRow icon={<CreditCard size={18} />} label="Save it with your email" onClick={go(() => set({ panel: "settings", panelArg: { tab: "account" } }))} />
            )}
            <SheetRow
              icon={<LogOut size={18} />}
              label="Sign out"
              onClick={() => {
                if (!ws?.email && !confirm("This board is only saved on this device. Signing out without an email means you cannot get back to it. Sign out anyway?")) return;
                closeSheet();
                void signOut();
              }}
            />
          </>
        )}
      </section>
    </Sheet>
  );
}

/**
 * A sticky at the top of the board's column, open to write in. It is drawn and given focus inside the tap itself, as
 * an iPhone shows its keyboard only for a field focused there.
 */
function addSticky() {
  const s = get();
  const mine = Object.values(s.items).filter((i) => i.boardId === s.boardId);
  const top = mine.length ? Math.min(...mine.map((i) => i.y)) : 0;
  const left = mine.length ? Math.min(...mine.map((i) => i.x)) : 0;
  let id: string | null = null;
  flushSync(() => {
    closeSheet();
    // above everything on the board, so the column puts it first
    const w = addWidget("sticky", { at: { x: left, y: top - 260 } });
    if (w) set({ editing: w.id, selection: [w.id] });
    id = w?.id ?? null;
  });
  if (!id) return;
  document.querySelector(".mobile")?.scrollTo({ top: 0 });
  document.querySelector<HTMLElement>(`[data-widget="${id}"] textarea`)?.focus();
}
